export const PAYMENT_LINK_HERO_IMAGE = '/assets/payments-link/hero-visual.png';

export const paymentLinkHero = {
  badge: 'Fast. Flexible. Built for modern businesses.',
  titleLine1: 'Accept Payments',
  titleHighlight: 'Instantly',
  titleLine2: 'with Payment Links',
  description:
    'Generate a link, share it anywhere—social media, SMS, or email—and get paid in seconds. No website required. No complex setup.',
  mobileDescriptionLine2:
    'get paid in seconds. No website required. No complex setup.',
  ctaLabel: 'Start Accepting Payments',
};

export const paymentLinkHowItWorksSection = {
  title: 'Three Simple Steps to Get Paid',
  subtitle:
    "We've removed the friction from payments so you can focus on building your business.",
  steps: [
    {
      id: 'create-link',
      number: 1,
      title: 'Create Your Link',
      description:
        'Set your price, add a product photo, and generate a unique link in seconds.',
    },
    {
      id: 'share-anywhere',
      number: 2,
      title: 'Share Anywhere',
      description:
        'Paste your link into Instagram, WhatsApp, or an email. Your customers click to pay.',
    },
    {
      id: 'get-paid',
      number: 3,
      title: 'Get Paid Instantly',
      description:
        'Funds are deposited into your account immediately. Track everything in real-time.',
    },
  ],
};

export const PAYMENT_LINK_LINKS_ICON = '/assets/payments-link/links.png';
export const PAYMENT_LINK_X_CIRCLE_ICON = '/assets/payments-link/x-circle.png';
export const PAYMENT_LINK_SHIELD_CHECK_ICON = '/assets/payments-link/shield-check.png';
export const PAYMENT_LINK_CHECK_CIRCLE_BLUE_ICON =
  '/assets/payments-link/check-circle-blue.png';
export const PAYMENT_LINK_CALCULATOR_ICON = '/assets/payments-link/calculator.png';
export const PAYMENT_LINK_BUILDING_ICON = '/assets/payments-link/building.png';
export const PAYMENT_LINK_BELL_ICON = '/assets/payments-link/bell (1).png';
export const PAYMENT_LINK_SHIELD_ICON = '/assets/payments-link/shield.png';
export const PAYMENT_LINK_LAYERS_ICON = '/assets/payments-link/layers.png';
export const PAYMENT_LINK_IMAGE_ICON = '/assets/payments-link/image (1).png';
export const PAYMENT_LINK_GLOBE_ICON = '/assets/payments-link/globe (1).png';
export const PAYMENT_LINK_BAR_CHART_ICON = '/assets/payments-link/bar-chart-2.png';

export const paymentLinkPricingComparisonSection = {
  title: 'Keep More From Every Payment',
  subtitleLine1:
    'Create payment links, sell products, receive payments, settle faster with zero monthly',
  subtitleLine2: 'fees and capped transaction charges',
  subtitleMobileLine1: 'Create payment links, sell products, receive',
  subtitleMobileLine2: 'payments, settle faster with zero monthly',
  subtitleMobileLine3: 'fees and capped transaction charges',
  recommendedPlan: {
    id: 'bizflex',
    icon: PAYMENT_LINK_LINKS_ICON,
    title: 'BizFlex Africa Payment Links',
    badge: 'RECOMMENDED',
    price: '₦0',
    priceSuffix: '/ month fee',
    features: [
      'No subscription required',
      'Instant settlement',
      'Transaction fee: 1%',
      'Minimum fee: ₦100',
      'Maximum fee capped at ₦1,500',
      'USD payment fee 2.5%',
      'Choose who pays the fee: you or your customer',
    ],
    footerIcon: PAYMENT_LINK_SHIELD_CHECK_ICON,
    footerNote:
      'No matter how large the payment is, the maximum NGN transaction fee is ₦1,500.',
  },
  alternativePlan: {
    id: 'alternative',
    icon: PAYMENT_LINK_BUILDING_ICON,
    title: 'Typical Alternative Platforms',
    price: '₦50k',
    priceSuffix: '/ month fee',
    priceNote: 'or more in subscription fees alone',
    features: [
      'Monthly subscription fees may range from ₦15,000 to ₦50,000',
      'Settlement may take T+1 or longer',
      'Transaction fees may be as high as 4% plus ₦100',
      'No clear fee cap in some cases',
      'USD transaction fees may go as high as 7.5%',
      'Limited flexibility on who pays transaction charges',
    ],
  },
};

export const paymentLinkExampleSavingsSection = {
  title: 'Example Savings',
  descriptionLine1:
    "On a ₦500,000 payment, BizFlex Africa's NGN fee is capped at ₦1,500. A platform charging 4% + ₦100 could charge ₦20,100. That means you could keep up to ",
  descriptionHighlight: '₦18,600',
  descriptionLine2: 'more on just one transaction.',
  descriptionMobileLine1: "On a ₦500,000 payment, BizFlex Africa's NGN fee is",
  descriptionMobileLine2:
    'capped at ₦1,500. A platform charging 4% + ₦100 could',
  descriptionMobileLine3: 'charge ₦20,100. That means you could keep up to',
  descriptionMobileLine4: 'more on just one transaction.',
  benefits: [
    'No monthly commitment',
    'Instant settlement',
    'Lower capped fees',
    'Flexible charge control',
  ],
  audienceText:
    'Built for freelancers, creators, merchants, and growing businesses.',
  ctaLabel: 'Create Payment Link',
};

export const paymentLinkFeaturesSection = {
  title: 'Everything You Need to Get Paid',
  subtitle:
    'Built for businesses that want to scale without the technical complexity.',
  subtitleMobileLine1: 'Built for businesses that want to scale',
  subtitleMobileLine2: 'without the technical complexity.',
  cards: [
    {
      id: 'instant-notifications',
      icon: PAYMENT_LINK_BELL_ICON,
      title: 'Instant Notifications',
      description:
        'Get pinged the moment a customer clicks, pays, or fails a transaction.',
      descriptionLine1: 'Get pinged the moment a customer',
      descriptionLine2: 'clicks, pays, or fails a transaction.',
      descriptionMobileLine1: 'Get pinged the moment a customer',
      descriptionMobileLine2: 'clicks, pays, or fails a transaction',
    },
    {
      id: 'secure-payments',
      icon: PAYMENT_LINK_SHIELD_ICON,
      title: 'Secure Payments',
      description:
        'Enterprise-grade security with end-to-end encryption for every link.',
      descriptionLine1: 'Enterprise-grade security with end-',
      descriptionLine2: 'to-end encryption for every link.',
      descriptionMobileLine1: 'Enterprise-grade security with end-to-',
      descriptionMobileLine2: 'end encryption for every link',
    },
    {
      id: 'custom-branding',
      icon: PAYMENT_LINK_IMAGE_ICON,
      title: 'Custom Branding',
      description:
        'Add your logo, brand colors, and domain to every storefront.',
      descriptionMobileLine1: 'Add your logo, brand colors, and',
      descriptionMobileLine2: 'domain to every storefront',
    },
    {
      id: 'multi-currency',
      icon: PAYMENT_LINK_GLOBE_ICON,
      title: 'Multi-Currency',
      description:
        'Seamlessly switch between NGN and USD to reach local and global audiences.',
      descriptionMobileLine1: 'Seamlessly switch between NGN and',
      descriptionMobileLine2: 'USD to reach local and global',
      descriptionMobileLine3: 'audiences',
    },
    {
      id: 'analytics-dashboard',
      icon: PAYMENT_LINK_BAR_CHART_ICON,
      title: 'Analytics Dashboard',
      description:
        'Deep insights into your conversion rates and highest-performing links.',
    },
    {
      id: 'easy-integration',
      icon: PAYMENT_LINK_LAYERS_ICON,
      title: 'Easy Integration',
      description:
        'API-first design that plugs into your existing tools in minutes.',
    },
  ],
};

export const PAYMENT_LINK_STAR_ICON = '/assets/payments-link/starr.png';
export const PAYMENT_LINK_TESTIMONIAL_AVATAR_1 = '/assets/payments-link/Amara.png';
export const PAYMENT_LINK_TESTIMONIAL_AVATAR_2 = '/assets/payments-link/David.png';

export const paymentLinkTestimonialsSection = {
  title: 'What Our Customers Say',
  mobileTitle: 'Customer Stories',
  subtitle: 'Join thousands of entrepreneurs already growing with BizFlex.',
  testimonials: [
    {
      id: 'amara-okafor',
      quote:
        'Payment links changed everything for us. I just send a link and get paid instantly — no more chasing invoices or waiting days for bank transfers.',
      quoteLines: [
        'Payment links changed everything for us. I just send a',
        'link and get paid instantly — no more chasing invoices or',
        'waiting days for bank transfers.',
      ],
      name: 'Amara Okafor',
      role: 'Creative Director',
      mobileAuthorLabel: 'Business Owner',
      mobileAuthorStyle: 'role-only',
      avatar: PAYMENT_LINK_TESTIMONIAL_AVATAR_1,
    },
    {
      id: 'david-okoro',
      quote:
        "I share payment links on WhatsApp and my clients pay in seconds. It's the fastest way to collect money — my cash flow has never been better.",
      quoteLines: [
        'I share payment links on WhatsApp and my clients pay in',
        "seconds. It's the fastest way to collect money — my cash",
        'flow has never been better.',
      ],
      name: 'David Okoro',
      role: 'SaaS Founder',
      mobileAuthorStyle: 'full',
      avatar: PAYMENT_LINK_TESTIMONIAL_AVATAR_2,
    },
  ],
};

export const paymentLinkStorefrontProducts = [
  {
    id: 'service-e',
    image: '/assets/payments-link/serviceE.png',
    alt: 'Service E',
  },
  {
    id: 'product-a',
    image: '/assets/invoice/productA.png',
    alt: 'Product A',
  },
  {
    id: 'product-b',
    image: '/assets/invoice/productB.png',
    alt: 'Product B',
  },
  {
    id: 'product-c',
    image: '/assets/payments-link/productC.png',
    alt: 'Product C',
  },
  {
    id: 'service-d',
    image: '/assets/payments-link/serviceD.png',
    alt: 'Service D',
  },
  {
    id: 'service-f',
    image: '/assets/payments-link/serviceF.png',
    alt: 'Service F',
  },
];

export const paymentLinkFinalCtaSection = {
  title: 'Ready to Scale Your Business?',
  description:
    'Start accepting payments globally today. Join the modern fintech revolution.',
  descriptionMobileLine1: 'Start accepting payments globally today.',
  descriptionMobileLine2: 'Join the modern fintech revolution.',
  ctaLabel: 'Get Started',
  learnMoreLabel: 'Learn more about BizFlex',
  learnMoreHref: '/',
};
